import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Upload, FileSpreadsheet, Trash2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Visualisateur d'export CSV fournisseur — le CSV exporté par le
// fournisseur (colonnes post_title/ID/stock/meta:bac/meta:colonne/
// meta:piece/meta:tiroir) est ici mappé vers les champs métier utilisés
// pour l'inventaire physique : Nom, UGS, Quantité, Allée (meta:piece),
// Colonne, Tiroir, Position (meta:bac). Le fichier est parsé entièrement
// CÔTÉ NAVIGATEUR (papaparse) : seul le résultat déjà structuré est envoyé
// au serveur.
const COLUMN_MAP = {
  nom: "post_title",
  ugs: "ID",
  quantite: "stock",
  allee: "meta:piece",
  colonne: "meta:colonne",
  tiroir: "meta:tiroir",
  position: "meta:bac",
};

export default function DepotCsvListPage() {
  const navigate = useNavigate();
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { fait, total } | null
  const fileInputRef = useRef(null);

  // Taille des lots envoyés au serveur — un fichier de plusieurs milliers de
  // lignes envoyé en une seule requête JSON pouvait dépasser une limite de
  // taille de requête intermédiaire (proxy/serveur web), provoquant une
  // erreur générique "Une erreur est survenue." sans détail exploitable.
  // Découper l'envoi en petits lots successifs contourne ce problème quelle
  // que soit la configuration exacte du serveur.
  const TAILLE_LOT = 1000;

  const load = async () => {
    try {
      const { data } = await api.get("/depot/csv-exports");
      setExports(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const lignes = results.data
            .filter((row) => (row[COLUMN_MAP.nom] || "").trim())
            .map((row) => ({
              nom: (row[COLUMN_MAP.nom] || "").trim(),
              ugs: (row[COLUMN_MAP.ugs] || "").trim(),
              quantite: parseInt(row[COLUMN_MAP.quantite], 10) || 0,
              allee: (row[COLUMN_MAP.allee] || "").trim(),
              colonne: (row[COLUMN_MAP.colonne] || "").replace(/\.0$/, "").trim(),
              tiroir: (row[COLUMN_MAP.tiroir] || "").replace(/\.0$/, "").trim(),
              position: (row[COLUMN_MAP.position] || "").trim(),
            }));
          if (lignes.length === 0) {
            toast.error("Aucune ligne exploitable trouvée dans ce fichier (colonnes attendues : post_title, ID, stock, meta:piece, meta:colonne, meta:tiroir, meta:bac)");
            return;
          }
          setImportProgress({ fait: 0, total: lignes.length });
          const premierLot = lignes.slice(0, TAILLE_LOT);
          const { data: created } = await api.post("/depot/csv-exports", { nom_fichier: file.name, lignes: premierLot });
          setImportProgress({ fait: premierLot.length, total: lignes.length });
          for (let i = TAILLE_LOT; i < lignes.length; i += TAILLE_LOT) {
            const lot = lignes.slice(i, i + TAILLE_LOT);
            await api.post(`/depot/csv-exports/${created.id}/append-lignes`, { lignes: lot });
            setImportProgress({ fait: Math.min(i + TAILLE_LOT, lignes.length), total: lignes.length });
          }
          toast.success(`Export importé (${lignes.length} article(s))`);
          navigate(`/depot/inventaire/${created.id}`);
        } catch (err) {
          if (!err.response) {
            toast.error("Impossible de contacter le serveur pendant l'import (connexion coupée ou lot encore trop volumineux). Réessayez, ou contactez un administrateur si le problème persiste.");
          } else {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        } finally {
          setImporting(false);
          setImportProgress(null);
        }
      },
      error: (err) => {
        toast.error(`Impossible de lire ce fichier : ${err.message}`);
        setImporting(false);
      },
    });
  };

  const removeExport = async (id) => {
    if (!window.confirm("Supprimer cet export ?")) return;
    try {
      await api.delete(`/depot/csv-exports/${id}`);
      toast.success("Export supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Inventaire CSV — Dépôt">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Link to="/depot">
          <Button variant="ghost" size="icon" data-testid="depot-csv-back"><ArrowLeft size={18} /></Button>
        </Link>
        <h1 className="font-heading text-lg font-semibold mr-auto">Inventaire CSV</h1>
        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChosen} data-testid="depot-csv-file-input" />
        <Button onClick={() => fileInputRef.current?.click()} disabled={importing} className="gap-2" data-testid="depot-csv-add-button">
          {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {importProgress ? `Import... ${importProgress.fait}/${importProgress.total}` : "Ajouter un export"}
        </Button>
      </div>
      {importProgress && (
        <p className="text-xs text-muted-foreground mb-4" data-testid="depot-csv-import-progress">
          Import en cours par lots (fichier volumineux) — ne fermez pas cette page.
        </p>
      )}

      <p className="text-sm text-muted-foreground mb-4">
        Importez l'export CSV du fournisseur pour vérifier physiquement le stock, allée par allée. Chaque article
        peut être coché "Vérifié" (ligne verte), et sa quantité constatée corrigée si elle diffère (ligne orange).
      </p>

      {loading && <p className="text-sm text-muted-foreground">Chargement...</p>}
      {!loading && exports.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="depot-csv-empty">Aucun export importé pour le moment.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="depot-csv-list">
        {exports.map((exp) => (
          <div key={exp.id} className="rounded-xl border border-border bg-card p-4 relative" data-testid={`depot-csv-item-${exp.id}`}>
            <button
              type="button" onClick={() => removeExport(exp.id)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-destructive"
              data-testid={`depot-csv-delete-${exp.id}`}
            >
              <Trash2 size={15} />
            </button>
            <Link to={`/depot/inventaire/${exp.id}`} className="block pr-6">
              <p className="font-heading font-semibold flex items-center gap-2 min-w-0 mb-1">
                <FileSpreadsheet size={17} className="shrink-0" /><span className="truncate">{exp.nom_fichier}</span>
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                {new Date(exp.created_at).toLocaleDateString("fr-FR")} · {exp.created_by_nom} · {exp.nb_lignes} article(s)
              </p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 size={13} />{exp.nb_verifiees} vérifié(s)</span>
                {exp.nb_ecarts > 0 && (
                  <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={13} />{exp.nb_ecarts} écart(s)</span>
                )}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </Layout>
  );
}
